import { apiClient } from './request';
import { create, fromJson, toJson, type JsonValue } from '@bufbuild/protobuf';
import type { Job, JobRun } from '@/proto/agentplatform/v1/job_pb';
import type { ListRequest } from '@/proto/agentplatform/httpv2/common_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  ListJobsResponseSchema,
  ListJobRunsResponseSchema,
  GetJobRequestSchema,
  GetJobResponseSchema,
  DeleteJobRequestSchema,
  DeleteJobResponseSchema,
  PauseJobRequestSchema,
  PauseJobResponseSchema,
  ResumeJobRequestSchema,
  ResumeJobResponseSchema,
  GetJobRunRequestSchema,
  GetJobRunResponseSchema,
} from '@/proto/agentplatform/httpv2/harness_pb';

export const jobApi = {
  // 过滤（type / status）通过共享 ListRequest.filter 透传，由后端按 JobModel 白名单校验。
  listJobs: async (params: ListRequest): Promise<{ data: Job[]; total: number }> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListJobs',
      toJson(ListRequestSchema, params)
    );
    const response = fromJson(ListJobsResponseSchema, responseObject.data);
    return { data: response.data?.items ?? [], total: response.data?.total ?? 0 };
  },

  getJob: async (jobId: string): Promise<Job> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetJob',
      toJson(GetJobRequestSchema, create(GetJobRequestSchema, { id: jobId }))
    );
    const response = fromJson(GetJobResponseSchema, responseObject.data);
    if (!response.data) {
      throw new Error('empty response data');
    }
    return response.data;
  },

  deleteJob: async (jobId: string): Promise<void> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteJob',
      toJson(DeleteJobRequestSchema, create(DeleteJobRequestSchema, { id: jobId }))
    );
    fromJson(DeleteJobResponseSchema, responseObject.data); // 仅做格式校验
  },

  pauseJob: async (jobId: string): Promise<Job> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/PauseJob',
      toJson(PauseJobRequestSchema, create(PauseJobRequestSchema, { id: jobId }))
    );
    const response = fromJson(PauseJobResponseSchema, responseObject.data);
    if (!response.data) {
      throw new Error('empty response data');
    }
    return response.data;
  },

  resumeJob: async (jobId: string): Promise<Job> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ResumeJob',
      toJson(ResumeJobRequestSchema, create(ResumeJobRequestSchema, { id: jobId }))
    );
    const response = fromJson(ResumeJobResponseSchema, responseObject.data);
    if (!response.data) {
      throw new Error('empty response data');
    }
    return response.data;
  },

  // 父任务 job_id（及可选 status）通过共享 ListRequest.filter 透传。
  listJobRuns: async (params: ListRequest): Promise<{ data: JobRun[]; total: number }> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListJobRuns',
      toJson(ListRequestSchema, params)
    );
    const response = fromJson(ListJobRunsResponseSchema, responseObject.data);
    return { data: response.data?.items ?? [], total: response.data?.total ?? 0 };
  },

  getJobRun: async (params: { jobId: string; runId: string }): Promise<JobRun> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetJobRun',
      toJson(
        GetJobRunRequestSchema,
        create(GetJobRunRequestSchema, { jobId: params.jobId, runId: params.runId })
      )
    );
    const response = fromJson(GetJobRunResponseSchema, responseObject.data);
    if (!response.data) {
      throw new Error('empty response data');
    }
    return response.data;
  },
};
